import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { FaTrash } from "react-icons/fa";
import { db } from "../Configuration/firebase";
import { isValidPhone, normalizePhone } from "../utils/phone";
import { saveTrustedContacts, MAX_TRUSTED_CONTACTS } from "../utils/safety";
import "../CSS/App.css";

// Manage the people who get texted on check-in / check-out / emergency.
const TrustedContacts = () => {
  const navigate = useNavigate();
  const [uid, setUid] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }
      setUid(user.uid);
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) setContacts(snap.data().trustedContacts || []);
      setLoading(false);
    });
    return () => unsub();
  }, [navigate]);

  const persist = async (next) => {
    setSaving(true);
    setError("");
    try {
      await saveTrustedContacts(uid, next);
      setContacts(next);
    } catch (err) {
      console.error("Error saving contacts:", err);
      setError("Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    if (!name.trim()) {
      setError("Enter a name.");
      return;
    }
    if (!isValidPhone(phone)) {
      setError(
        "Enter a full international number, including country code (e.g. +14155552671).",
      );
      return;
    }
    if (contacts.length >= MAX_TRUSTED_CONTACTS) {
      setError(`You can add up to ${MAX_TRUSTED_CONTACTS} contacts.`);
      return;
    }
    const normalized = normalizePhone(phone);
    if (contacts.some((c) => c.phone === normalized)) {
      setError("That number is already in your list.");
      return;
    }
    await persist([...contacts, { name: name.trim(), phone: normalized }]);
    setName("");
    setPhone("");
  };

  const handleRemove = async (phoneToRemove) => {
    await persist(contacts.filter((c) => c.phone !== phoneToRemove));
  };

  return (
    <div
      className="container"
      style={{
        width: "85%",
        maxWidth: "420px",
        margin: "auto",
        paddingTop: "calc(20px + env(safe-area-inset-top, 0px))",
      }}
    >
      <h2>Trusted Contacts</h2>
      <p style={{ color: "var(--text-secondary, #777)", fontSize: "14px" }}>
        These people are texted when you check in, check out, or send an
        emergency alert.
      </p>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              marginBottom: "10px",
            }}
          >
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name"
              style={{
                padding: "10px 15px",
                borderRadius: "12px",
                border: "1px solid var(--border-color, #ccc)",
                backgroundColor: "var(--input-bg, #fff)",
                color: "var(--text-primary, #000)",
              }}
            />
            <input
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 415 555 2671"
              style={{
                padding: "10px 15px",
                borderRadius: "12px",
                border: "1px solid var(--border-color, #ccc)",
                backgroundColor: "var(--input-bg, #fff)",
                color: "var(--text-primary, #000)",
              }}
            />
          </div>
          {error && <p style={{ color: "red" }}>{error}</p>}
          <button
            type="button"
            onClick={handleAdd}
            disabled={saving}
            style={{
              padding: "10px 24px",
              border: "none",
              cursor: saving ? "default" : "pointer",
              borderRadius: "5px",
              backgroundColor: "#4CAF50",
              color: "white",
              marginBottom: "18px",
            }}
          >
            {saving ? "Saving..." : "Add Contact"}
          </button>

          {contacts.length === 0 ? (
            <p style={{ fontSize: "14px" }}>No contacts yet.</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {contacts.map((c) => (
                <li
                  key={c.phone}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 0",
                    borderBottom: "1px solid var(--border-color, #eee)",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600 }}>{c.name}</div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "var(--text-secondary, #777)",
                      }}
                    >
                      {c.phone}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemove(c.phone)}
                    disabled={saving}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#DC3545",
                    }}
                    aria-label={`Remove ${c.name}`}
                  >
                    <FaTrash size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <button
        type="button"
        onClick={() => navigate("/safety")}
        style={{
          backgroundColor: "gray",
          color: "white",
          padding: "10px 15px",
          border: "none",
          cursor: "pointer",
          borderRadius: "5px",
          marginTop: "18px",
        }}
      >
        Back
      </button>
    </div>
  );
};

export default TrustedContacts;
